import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Users, Clock, Ticket, ShieldCheck, Loader2, RefreshCw, X } from 'lucide-react';
import Navbar from '../components/Navbar';
import MobileBottomNav from '../components/MobileBottomNav';
import { eventsApi } from '../lib/api';

const QueuePage = () => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [queuePosition, setQueuePosition] = useState(null);
  const [estimatedWait, setEstimatedWait] = useState(null);
  const [totalInQueue, setTotalInQueue] = useState(0);
  const [status, setStatus] = useState('waiting'); // waiting, ready, sold_out
  const [checkingQueue, setCheckingQueue] = useState(false);

  // Generate or retrieve queue token
  const getQueueToken = useCallback(() => {
    const storageKey = `queue_token_${eventId}`;
    let token = sessionStorage.getItem(storageKey);
    if (!token) {
      token = `qt_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      sessionStorage.setItem(storageKey, token);
    }
    return token;
  }, [eventId]);

  // Load event data
  useEffect(() => {
    const loadEvent = async () => {
      try {
        const res = await eventsApi.getById(eventId);
        setEvent(res.data);
      } catch (error) {
        console.error('Error loading event:', error);
      } finally {
        setLoading(false);
      }
    };
    loadEvent();
  }, [eventId]);

  // Check queue status
  const checkQueueStatus = useCallback(async () => {
    if (!event) return;
    
    setCheckingQueue(true);
    try {
      const token = getQueueToken();
      const res = await eventsApi.checkQueue(eventId, token);
      
      if (res.data) {
        setQueuePosition(res.data.position);
        setEstimatedWait(res.data.estimated_wait);
        setTotalInQueue(res.data.total_in_queue);
        setStatus(res.data.status);
        
        // If ready, redirect to checkout
        if (res.data.status === 'ready') {
          const tier = searchParams.get('tier');
          const qty = searchParams.get('qty') || '1';
          navigate(`/evento/${eventId}/checkout?tier=${tier || ''}&qty=${qty}&queue_token=${token}`);
        }
      }
    } catch (error) {
      // If queue endpoint doesn't exist, go directly to checkout (queue disabled)
      if (error.response?.status === 404) {
        const tier = searchParams.get('tier');
        const qty = searchParams.get('qty') || '1';
        navigate(`/evento/${eventId}/checkout?tier=${tier || ''}&qty=${qty}`);
      }
      console.error('Queue check error:', error);
    } finally {
      setCheckingQueue(false);
    }
  }, [event, eventId, getQueueToken, navigate, searchParams]);

  // Initial queue check and polling
  useEffect(() => {
    if (event) {
      checkQueueStatus();
      
      // Poll every 5 seconds if waiting
      const interval = setInterval(() => {
        if (status === 'waiting') {
          checkQueueStatus();
        }
      }, 5000);
      
      return () => clearInterval(interval);
    }
  }, [event, status, checkQueueStatus]);

  // Format wait time
  const formatWaitTime = (seconds) => {
    if (!seconds) return 'Calculando...';
    if (seconds < 60) return 'Menos de 1 minuto';
    const minutes = Math.ceil(seconds / 60);
    return `~${minutes} minuto${minutes > 1 ? 's' : ''}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#030303] flex items-center justify-center">
        <Loader2 className="animate-spin text-[#D4AF37]" size={40} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#030303] text-white">
      <Navbar />
      
      <section className="min-h-screen flex items-center justify-center px-4 py-20">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="max-w-md w-full"
        >
          {/* Queue Card */}
          <div className="bg-gradient-to-br from-[#121212] to-[#0A0A0A] rounded-3xl border border-white/10 overflow-hidden">
            {/* Event Image Header */}
            {event?.image_url && (
              <div className="relative h-32 overflow-hidden">
                <img 
                  src={event.image_url} 
                  alt={event.title}
                  className="w-full h-full object-cover opacity-50"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#121212] to-transparent" />
                <div className="absolute bottom-3 left-4 right-4">
                  <h2 className="font-bold text-lg truncate">{event?.title}</h2>
                </div>
              </div>
            )}
            
            {/* Queue Status */}
            <div className="p-6 space-y-6">
              {status === 'waiting' && (
                <>
                  {/* Animated Queue Icon */}
                  <div className="flex justify-center">
                    <motion.div
                      animate={{ 
                        scale: [1, 1.05, 1],
                        opacity: [0.8, 1, 0.8]
                      }}
                      transition={{ duration: 2, repeat: Infinity }}
                      className="w-20 h-20 rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/30 flex items-center justify-center"
                    >
                      <Users className="text-[#D4AF37]" size={36} />
                    </motion.div>
                  </div>
                  
                  <div className="text-center space-y-2">
                    <h3 className="text-xl font-bold">Estás en la cola</h3>
                    <p className="text-white/50 text-sm">
                      Mantené esta página abierta. Te redirigiremos automáticamente cuando sea tu turno.
                    </p>
                  </div>
                  
                  {/* Queue Stats */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-white/5 rounded-xl p-4 text-center">
                      <div className="text-2xl font-bold text-[#D4AF37]">
                        #{queuePosition || '...'}
                      </div>
                      <div className="text-xs text-white/40 uppercase tracking-wider mt-1">
                        Tu posición
                      </div>
                    </div>
                    <div className="bg-white/5 rounded-xl p-4 text-center">
                      <div className="text-2xl font-bold text-white">
                        {formatWaitTime(estimatedWait)}
                      </div>
                      <div className="text-xs text-white/40 uppercase tracking-wider mt-1">
                        Espera estimada
                      </div>
                    </div>
                  </div>
                  
                  {/* Total in queue */}
                  <div className="flex items-center justify-center gap-2 text-white/40 text-sm">
                    <Users size={14} />
                    <span>{totalInQueue} personas en la cola</span>
                  </div>
                  
                  {/* Progress indicator */}
                  <div className="space-y-2">
                    <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
                      <motion.div
                        className="h-full bg-gradient-to-r from-[#D4AF37] to-[#F59E0B]"
                        initial={{ width: '0%' }}
                        animate={{ 
                          width: queuePosition && totalInQueue 
                            ? `${Math.max(5, 100 - (queuePosition / totalInQueue) * 100)}%` 
                            : '5%' 
                        }}
                        transition={{ duration: 0.5 }}
                      />
                    </div>
                    <div className="flex justify-between text-xs text-white/30">
                      <span>Esperando</span>
                      <span>Tu turno</span>
                    </div>
                  </div>
                  
                  {/* Manual refresh */}
                  <button
                    onClick={checkQueueStatus}
                    disabled={checkingQueue}
                    className="w-full flex items-center justify-center gap-2 py-3 text-white/50 hover:text-white transition-colors text-sm"
                  >
                    <RefreshCw size={14} className={checkingQueue ? 'animate-spin' : ''} />
                    <span>Actualizar estado</span>
                  </button>
                </>
              )}
              
              {status === 'ready' && (
                <div className="text-center space-y-4">
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    className="w-20 h-20 mx-auto rounded-full bg-green-500/20 border border-green-500/30 flex items-center justify-center"
                  >
                    <Ticket className="text-green-400" size={36} />
                  </motion.div>
                  <h3 className="text-xl font-bold text-green-400">¡Es tu turno!</h3>
                  <p className="text-white/50 text-sm">Redirigiendo al checkout...</p>
                  <Loader2 className="animate-spin mx-auto text-green-400" size={24} />
                </div>
              )}
              
              {status === 'sold_out' && (
                <div className="text-center space-y-4">
                  <div className="w-20 h-20 mx-auto rounded-full bg-red-500/20 border border-red-500/30 flex items-center justify-center">
                    <X className="text-red-400" size={36} />
                  </div>
                  <h3 className="text-xl font-bold text-red-400">Entradas agotadas</h3>
                  <p className="text-white/50 text-sm">
                    Lo sentimos, las entradas se agotaron mientras esperabas.
                  </p>
                  <button
                    onClick={() => navigate(`/evento/${eventId}`)}
                    className="px-6 py-3 bg-white/10 hover:bg-white/20 rounded-full transition-colors"
                  >
                    Volver al evento
                  </button>
                </div>
              )}
            </div>
            
            {/* Security badge */}
            <div className="px-6 pb-6">
              <div className="flex items-center justify-center gap-2 text-white/30 text-xs">
                <ShieldCheck size={12} />
                <span>Conexión segura - No cierres esta página</span>
              </div>
            </div>
          </div>
        </motion.div>
      </section>
      
      <MobileBottomNav />
    </div>
  );
};

export default QueuePage;
